import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Activity, LogOut, Play, LockKeyhole, ScanLine, ShieldAlert,
  Ticket, UserRoundCheck, DoorClosed, DoorOpen, Ban, RotateCcw,
  Camera, Fingerprint, Users, Search, CheckCircle2, XCircle,
  IdCard, ScrollText, Maximize2, ArrowLeft,
} from "lucide-react";

const REASON_LABEL = {
  underage: "Underage",
  expired_id: "Expired ID",
  watchlist: "On the watch list",
  invalid_ticket: "Invalid ticket",
  duplicate_scan: "Already scanned",
  capacity_reached: "Capacity reached",
  voided_ticket: "Ticket is void",
  manual: "Manual deny",
  reservation_used: "Reservation already checked in",
  reservation_cancelled: "Reservation cancelled",
  guest_list_used: "Guest already arrived",
};

const newPhotoHash = () => {
  const alpha = "abcdefghijklmnopqrstuvwxyz0123456789";
  let s = "id-";
  for (let i = 0; i < 12; i++) s += alpha[Math.floor(Math.random() * alpha.length)];
  return s;
};

export default function EntranceLandingPage({ focus = false }) {
  const { branchId, hasPerm } = useAuth();
  const [session, setSession] = useState(null);
  const [recent, setRecent] = useState([]);
  const [opening, setOpening] = useState(false);
  const [capacity, setCapacity] = useState("");   // Blank = inherit event capacity (spec §5)
  const [eventPicker, setEventPicker] = useState(null);
  const [pickedEventId, setPickedEventId] = useState("");

  const load = async () => {
    if (!branchId) return;
    try {
      const { data: sess } = await api.get("/entrance/sessions/current", { params: { branch_id: branchId } });
      setSession(sess);
      if (sess) {
        const { data: scans } = await api.get(`/entrance/sessions/${sess.id}/scans`, { params: { limit: 12 } });
        setRecent(scans);
      } else { setRecent([]); }
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [branchId]);

  const openSession = async (opts = {}) => {
    if (!branchId) return;
    setOpening(true);
    try {
      await api.post("/entrance/sessions/open", { branch_id: branchId, capacity: parseInt(capacity) || 0, ...opts });
      toast.success("Entrance session opened");
      setEventPicker(null); setPickedEventId("");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "multiple_events") {
        setEventPicker({ candidates: detail.candidates });
        toast.info("Multiple events tonight — pick one below.");
      } else { toast.error(formatApiError(detail)); }
    } finally { setOpening(false); }
  };

  const closeSession = async () => {
    if (!session || !window.confirm("Close the entrance session? Head count will be locked.")) return;
    try {
      await api.post(`/entrance/sessions/${session.id}/close`);
      toast.success("Session closed"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const registerExit = async () => {
    if (!session) return;
    try {
      const { data } = await api.post("/entrance/scan/exit", { session_id: session.id });
      toast.success(`Exit registered — occupancy ${data.new_occupancy}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!branchId) {
    return (
      <div className="p-14 max-w-4xl mx-auto">
        <div className="section-eyebrow">Entrance</div>
        <h1 className="mt-4 display-heading text-3xl ink">Select a branch first</h1>
        <p className="mt-2 ink-muted text-sm">Pick a branch from the top bar to open the door.</p>
      </div>
    );
  }

  return (
    <div className="min-h-full" data-testid="entrance-landing">
      <div className="max-w-6xl mx-auto px-6 py-8 lg:py-10">
        {/* Compact top bar — replaces the old sidebar */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <Link to="/" className="tiny-label ink-subtle hover:ink flex items-center gap-1"><ArrowLeft size={12}/> All modules</Link>
            <div className="mt-3 section-eyebrow">Entrance · Security</div>
            <h1 className="mt-1 display-heading text-3xl ink">Door</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/security/watchlist"
                    data-testid="entrance-open-watchlist"
                    title="Watchlist"
                    className="h-10 w-10 grid place-items-center rounded-md border hairline hover:bg-foreground/5 ink-muted hover:ink">
              <Ban size={14}/>
            </Link>
            <Link to="/security/log"
                    data-testid="entrance-open-log"
                    title="Entrance log"
                    className="h-10 w-10 grid place-items-center rounded-md border hairline hover:bg-foreground/5 ink-muted hover:ink">
              <ScrollText size={14}/>
            </Link>
            <Link to="/security/scanner"
                    data-testid="entrance-open-focus"
                    title="Kiosk (full-screen scanner)"
                    className="h-10 w-10 grid place-items-center rounded-md border hairline hover:bg-foreground/5 ink-muted hover:ink">
              <Maximize2 size={14}/>
            </Link>
          </div>
        </div>

        {/* CLOSED-DOOR STATE */}
        {!session && (
          <OpenSessionCard
            capacity={capacity} setCapacity={setCapacity}
            opening={opening} openSession={openSession}
            eventPicker={eventPicker} setEventPicker={setEventPicker}
            pickedEventId={pickedEventId} setPickedEventId={setPickedEventId}
            canOpen={hasPerm("entrance.session.manage")}
          />
        )}

        {/* OPEN-DOOR STATE — the workspace */}
        {session && (
          <>
            {!focus && (
              <SessionHeader session={session}
                              onExit={registerExit}
                              onClose={closeSession}
                              canClose={hasPerm("entrance.session.manage")}/>
            )}
            <div className={focus ? "" : "mt-8"}>
              <InlineScanner session={session} onScan={load}/>
            </div>
            {!focus && <RecentScans scans={recent}/>}
          </>
        )}
      </div>
    </div>
  );
}


// ==================================================================
// Open-session card (closed-door state)
// ==================================================================

function OpenSessionCard({ capacity, setCapacity, opening, openSession,
                            eventPicker, setEventPicker, pickedEventId, setPickedEventId,
                            canOpen }) {
  return (
    <div className="border hairline-soft rounded-lg p-8 lg:p-10 animate-fade-up animate-fade-up-1"
          data-testid="entrance-empty">
      <div className="flex items-start gap-6">
        <div className="h-14 w-14 grid place-items-center rounded-full border hairline surface-2">
          <ShieldAlert size={20} className="ink" strokeWidth={1.4}/>
        </div>
        <div className="flex-1">
          <div className="section-eyebrow">Door is closed</div>
          <h2 className="mt-2 display-heading text-2xl ink">Open the entrance session</h2>
          <p className="mt-2 ink-muted text-sm max-w-xl">
            Tonight's live event will auto-attach and its capacity inherited. Or open a walk-in night to skip.
          </p>
        </div>
      </div>

      {!eventPicker && canOpen && (
        <div className="mt-8 flex flex-wrap items-end gap-3">
          <label className="block">
            <div className="tiny-label">Capacity <span className="ink-subtle">(blank = inherit event)</span></div>
            <input type="number" min={0} placeholder="0"
                   value={capacity} onChange={(e) => setCapacity(e.target.value)}
                   className="mt-2 h-11 w-40 rounded-md border hairline surface-card px-3 mono ink text-lg"
                   data-testid="entrance-open-cap"/>
          </label>
          <button onClick={() => openSession()} disabled={opening}
                  data-testid="entrance-open-btn"
                  className="btn-pill btn-pill-solid h-11 disabled:opacity-40">
            <Play size={13}/> {opening ? "Opening…" : "Open door"}
          </button>
          <button onClick={() => openSession({ force_walkin: true })} disabled={opening}
                  data-testid="entrance-open-walkin-btn"
                  className="btn-pill btn-pill-outline h-11 disabled:opacity-40">
            Walk-in night (no event)
          </button>
        </div>
      )}

      {eventPicker && (
        <div className="mt-8" data-testid="entrance-event-picker">
          <div className="section-eyebrow mb-3">Which event is tonight?</div>
          <div className="border hairline-soft rounded-md divide-y hairline-soft">
            {eventPicker.candidates.map((c) => (
              <label key={c.id}
                      data-testid={`entrance-event-option-${c.id}`}
                      className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-foreground/5 ${pickedEventId === c.id ? "bg-foreground/5" : ""}`}>
                <input type="radio" name="event-pick" value={c.id}
                       checked={pickedEventId === c.id}
                       onChange={() => setPickedEventId(c.id)}/>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold ink">{c.name}</div>
                  <div className="mt-1 mono text-xs ink-subtle">
                    {new Date(c.starts_at).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" })}
                    {" · "}<span className="uppercase">{c.status}</span>
                    {c.capacity ? ` · cap ${c.capacity}` : ""}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={() => openSession({ event_id: pickedEventId })}
                    disabled={opening || !pickedEventId}
                    data-testid="entrance-picker-confirm"
                    className="btn-pill btn-pill-solid h-11 disabled:opacity-40">
              <Play size={13}/> Open door for selected event
            </button>
            <button onClick={() => { setEventPicker(null); setPickedEventId(""); }}
                    className="btn-pill btn-pill-outline h-11">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================================================================
// Session header — big occupancy + primary actions
// ==================================================================

function SessionHeader({ session, onExit, onClose, canClose }) {
  const cap = session.capacity ?? 0;
  const occ = session.current_occupancy ?? 0;
  const pct = cap ? Math.round((occ / cap) * 100) : 0;
  const barColor = pct > 95 ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="animate-fade-up animate-fade-up-1" data-testid="entrance-session-header">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7 border hairline surface-card rounded-lg p-6 lg:p-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="section-eyebrow">Live occupancy</div>
              {session.event_name && (
                <div className="mt-2 text-xs ink-muted flex items-center gap-1.5" data-testid="entrance-event-label">
                  <DoorOpen size={12}/> {session.event_name}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onExit} data-testid="entrance-exit-btn"
                      className="btn-pill btn-pill-outline h-9"><LogOut size={12}/> Exit</button>
              {canClose && (
                <button onClick={onClose} data-testid="entrance-close-btn"
                        className="btn-pill btn-pill-outline h-9 text-red-500 border-red-500/40 hover:bg-red-500/5">
                  <LockKeyhole size={12}/> Close
                </button>
              )}
            </div>
          </div>

          <div className="mt-5 flex items-end gap-2">
            <div className={`display-heading text-7xl mono ${pct > 95 ? "text-red-500" : pct > 80 ? "text-amber-500" : "ink"}`}
                  data-testid="entrance-live-occupancy">
              {occ}
            </div>
            <div className="pb-3 ink-subtle text-2xl mono">/{cap || "∞"}</div>
          </div>
          {cap > 0 && (
            <div className="mt-4 h-1.5 rounded-full bg-foreground/5 overflow-hidden">
              <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }}/>
            </div>
          )}
          <div className="mt-4 grid grid-cols-3 gap-6 text-sm">
            <MetricMini k="Scans" v={session.scans_total || 0}/>
            <MetricMini k="Allowed" v={session.scans_allowed || 0} tone="emerald"/>
            <MetricMini k="Denied" v={session.scans_denied || 0} tone="red"/>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 border hairline-soft rounded-lg p-6 lg:p-8 space-y-4">
          <div className="section-eyebrow">Session</div>
          <FactRow k="Opened at" v={session.opened_at ? new Date(session.opened_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}/>
          <FactRow k="Event" v={session.event_name || "Walk-in night"}/>
          <FactRow k="Capacity" v={cap ? `${cap} pax` : "∞ (no cap)"}/>
          <FactRow k="Exits" v={session.exits || 0}/>
        </div>
      </div>
    </div>
  );
}


// ==================================================================
// Inline scanner — the primary work surface (was EntranceDoorPage)
// ==================================================================

function InlineScanner({ session, onScan }) {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("scan"); // scan | walkin
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewHeads, setPreviewHeads] = useState(0);
  const [photoHash, setPhotoHash] = useState("");
  const [override, setOverride] = useState({ open: false, reason: "" });
  const [lastAttempt, setLastAttempt] = useState(null); // {source, ...args} for override routing
  const inputRef = useRef(null);

  const focusInput = () => setTimeout(() => inputRef.current?.focus(), 50);
  const softReset = () => { setResult(null); setPreview(null); setInput(""); setPhotoHash(""); focusInput(); };
  const hardReset = () => { softReset(); setOverride({ open: false, reason: "" }); };

  useEffect(() => {
    if (result?.allowed && !result?.override_used) {
      const t = setTimeout(() => softReset(), 3000);
      return () => clearTimeout(t);
    }
  }, [result]); // eslint-disable-line

  const submitLookup = async () => {
    if (!input.trim() || busy) return;
    setBusy(true); setResult(null); setPreview(null);
    try {
      const { data } = await api.post("/entrance/lookup", { session_id: session.id, code: input.trim() });
      if (data.source_type === "ticket") await submitTicketScan(input.trim());
      else if (data.source_type === "guest_list" && data.guest_list_entry) await submitGuestListScan(data.guest_list_entry.id);
      else if (data.source_type === "guest_list" && data.candidates) {
        // Selectable candidate list (spec §7) — do not just toast an error.
        setPreview({ kind: "guest_list_candidates", data: { candidates: data.candidates } });
      } else if (data.source_type === "reservation") {
        setPreview({ kind: "reservation", data: data.reservation });
        setPreviewHeads(data.reservation.guest_count || 1);
      } else if (data.source_type === "staff") {
        // Try to clock the staff in/out (spec §8, small reuse of HR).
        try {
          const { data: att } = await api.post("/entrance/scan/staff", {
            session_id: session.id, code: input.trim(),
          });
          setPreview({ kind: "staff", data: { ...data.staff, attendance: att } });
        } catch (_) {
          setPreview({ kind: "staff", data: { ...data.staff, attendance: null } });
        }
      } else {
        toast.error("Unknown code — try walk-in ID.");
        setPreview({ kind: "unknown", data: { code: data.code } });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitTicketScan = async (code, overrideReason = "") => {
    setBusy(true); setLastAttempt({ source: "ticket", code });
    try {
      const { data } = await api.post("/entrance/scan/ticket", {
        session_id: session.id, code, photo_hash: photoHash,
        override: !!overrideReason, override_reason: overrideReason,
      });
      handleVerdict(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setOverride({ open: false, reason: "" }); }
  };

  const submitGuestListScan = async (entryId, headsOverride, overrideReason = "") => {
    setBusy(true); setLastAttempt({ source: "guest_list", entryId, headsOverride });
    try {
      const { data } = await api.post("/entrance/scan/guest-list", {
        session_id: session.id, entry_id: entryId,
        heads_override: headsOverride || null,
        override: !!overrideReason, override_reason: overrideReason,
      });
      handleVerdict(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setOverride({ open: false, reason: "" }); }
  };

  const submitReservationScan = async (reservationId, headsOverride, overrideReason = "") => {
    setBusy(true); setLastAttempt({ source: "reservation", reservationId, headsOverride });
    try {
      const { data } = await api.post("/entrance/scan/reservation", {
        session_id: session.id, reservation_id: reservationId,
        heads_override: headsOverride || null,
        override: !!overrideReason, override_reason: overrideReason,
      });
      handleVerdict(data); setPreview(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setOverride({ open: false, reason: "" }); }
  };

  const submitWalkIn = async (overrideReason = "") => {
    if (!photoHash || busy) return;
    setBusy(true); setLastAttempt({ source: "walkin" });
    try {
      const { data } = await api.post("/entrance/scan/walk-in", {
        session_id: session.id, photo_hash: photoHash,
        override: !!overrideReason, override_reason: overrideReason,
      });
      handleVerdict(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setOverride({ open: false, reason: "" }); }
  };

  // Route the manager override to the ORIGINAL scan source (spec §9).
  const submitOverride = async () => {
    if (!override.reason.trim() || !lastAttempt) return;
    const reason = override.reason.trim();
    if (lastAttempt.source === "ticket") await submitTicketScan(lastAttempt.code, reason);
    else if (lastAttempt.source === "guest_list") await submitGuestListScan(lastAttempt.entryId, lastAttempt.headsOverride, reason);
    else if (lastAttempt.source === "reservation") await submitReservationScan(lastAttempt.reservationId, lastAttempt.headsOverride, reason);
    else await submitWalkIn(reason);
  };

  const handleVerdict = (data) => {
    setResult(data); onScan();
    if (data.allowed) toast.success(`Admitted · ${data.heads_admitted || 1} head${data.heads_admitted > 1 ? "s" : ""}`);
    else toast.error(`Denied — ${REASON_LABEL[data.denial?.reason] || data.denial?.reason || "unknown"}`);
  };

  return (
    <div className="border hairline surface-card rounded-lg p-6 lg:p-8 animate-fade-up animate-fade-up-2"
          data-testid="entrance-scanner-page">
      {/* Tabs */}
      <div className="flex items-center gap-1 border hairline-soft rounded-full p-1 w-max mb-6">
        {[
          { k: "scan", label: "Scan / Search", icon: ScanLine },
          { k: "walkin", label: "Walk-in ID", icon: IdCard },
        ].map((m) => (
          <button key={m.k} onClick={() => { setTab(m.k); hardReset(); }}
                  data-testid={`scanner-tab-${m.k}`}
                  className={`h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold flex items-center gap-2 transition-all ${
                    tab === m.k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"
                  }`}>
            <m.icon size={12}/> {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT — capture */}
        <div>
          {tab === "scan" ? (
            <>
              <div className="section-eyebrow flex items-center gap-2"><Search size={12}/> Scan QR or enter code</div>
              <input autoFocus ref={inputRef}
                     value={input}
                     onChange={(e) => setInput(e.target.value.toUpperCase())}
                     onKeyDown={(e) => e.key === "Enter" && !busy && submitLookup()}
                     data-testid="scanner-universal-input"
                     placeholder="TIX-XXXXXX · OWL-XXXXXX · Guest name"
                     className="mt-3 h-20 w-full rounded-md border hairline surface-2 px-4 mono ink text-2xl font-bold tracking-widest text-center"/>
              <div className="mt-2 tiny-label ink-subtle text-center">
                Ticket · reservation · guest list · staff badge — all in one field.
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={submitLookup} disabled={busy || !input.trim()}
                        data-testid="scanner-submit-btn"
                        className="btn-pill btn-pill-solid h-14 flex-1 text-sm disabled:opacity-40">
                  <ScanLine size={16}/> {busy ? "Checking…" : "Search / Scan"}
                </button>
                {(result || preview) && (
                  <button onClick={hardReset} data-testid="scanner-reset"
                          className="btn-pill btn-pill-outline h-14 px-6"><RotateCcw size={14}/></button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="section-eyebrow flex items-center gap-2"><IdCard size={12}/> Walk-in ID verification</div>
              <div className="mt-3 tiny-label ink-subtle">
                Use this when the guest is not on any list and has no ticket.
              </div>
              <div className="mt-6">
                <div className="tiny-label">ID capture</div>
                <div className="mt-2 flex items-center gap-3">
                  <button onClick={() => setPhotoHash(newPhotoHash())} type="button"
                          data-testid="scanner-capture-id"
                          className="btn-pill btn-pill-outline h-11 flex-1"><Camera size={13}/> Capture ID</button>
                  {photoHash && (
                    <button onClick={() => setPhotoHash("")} className="tiny-label ink-subtle hover:ink" data-testid="scanner-clear-id">Clear</button>
                  )}
                </div>
                {photoHash && (
                  <div className="mt-3 tiny-label ink-subtle mono truncate">hash: {photoHash}</div>
                )}
              </div>
              <button onClick={() => submitWalkIn()} disabled={busy || !photoHash}
                      data-testid="scanner-submit-walkin"
                      className="mt-8 btn-pill btn-pill-solid h-14 w-full text-sm disabled:opacity-40">
                <Fingerprint size={16}/> Verify walk-in
              </button>
            </>
          )}
        </div>

        {/* RIGHT — verdict / preview */}
        <div className="min-h-[280px]" data-testid="scanner-result-card">
          {!result && !preview && (
            <div className="mt-8 text-center ink-subtle text-sm">
              <div className="section-eyebrow mb-3">Verdict</div>
              Result will appear here after each scan.
            </div>
          )}

          {preview?.kind === "guest_list_candidates" && (
            <div data-testid="scanner-guest-list-candidates">
              <div className="section-eyebrow">Multiple guest-list matches</div>
              <div className="mt-3 tiny-label ink-subtle">Pick the guest to admit.</div>
              <div className="mt-4 divide-y hairline-soft border hairline rounded-md">
                {preview.data.candidates.map((c) => (
                  <button key={c.id}
                          onClick={() => submitGuestListScan(c.id)}
                          data-testid={`scanner-gl-candidate-${c.id}`}
                          className="w-full text-left p-3 hover:bg-foreground/5 disabled:opacity-40"
                          disabled={busy}>
                    <div className="text-sm ink font-semibold">{c.name}</div>
                    <div className="mt-1 mono text-[10px] ink-subtle">
                      {c.list_name}
                      {c.promoter_name ? ` · ${c.promoter_name}` : ""}
                      {" · "}{1 + (c.plus_ones || 0)} head{c.plus_ones ? "s" : ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {preview?.kind === "reservation" && (
            <div data-testid="scanner-reservation-preview">
              <div className="section-eyebrow">Reservation found</div>
              <div className="mt-3 display-heading text-2xl ink">{preview.data.customer_name}</div>
              <div className="mt-1 mono ink-subtle text-xs">{preview.data.confirmation_code}</div>
              <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <FactRow k="Expected" v={`${preview.data.guest_count || 1} guests`}/>
                <FactRow k="Deposit" v={peso(preview.data.deposit_paid || 0)}/>
                <FactRow k="Table" v={preview.data.table_id ? preview.data.table_id.slice(-6) : "—"}/>
                <FactRow k="Status" v={preview.data.status}/>
              </div>
              <label className="block mt-4">
                <div className="tiny-label">Actual heads arriving</div>
                <input type="number" min={1} value={previewHeads}
                       onChange={(e) => setPreviewHeads(parseInt(e.target.value || 1))}
                       data-testid="scanner-heads-input"
                       className="mt-2 h-12 w-full rounded-md border hairline surface-2 px-4 mono ink text-xl font-bold text-center"/>
              </label>
              <button onClick={() => submitReservationScan(preview.data.id, previewHeads)}
                      disabled={busy}
                      data-testid="scanner-confirm-reservation"
                      className="mt-4 btn-pill btn-pill-solid h-12 w-full text-sm disabled:opacity-40">
                <Users size={14}/> Arrive all guests · {previewHeads} heads
              </button>
            </div>
          )}

          {preview?.kind === "staff" && (
            <div data-testid="scanner-staff-preview" className="text-center pt-4">
              <IdCard size={40} className="mx-auto ink" strokeWidth={1.2}/>
              <div className="mt-4 display-heading text-2xl ink">{preview.data.name}</div>
              <div className="mt-1 tiny-label ink-subtle">{preview.data.position} · staff badge</div>
              {preview.data.attendance?.ok ? (
                <div className="mt-4 rounded-md hairline border p-3 bg-emerald-500/5 border-emerald-500/30 inline-block"
                     data-testid="scanner-staff-attendance">
                  <div className="tiny-label text-emerald-600">
                    {preview.data.attendance.now_on_clock ? "Clocked in" : "Clocked out"}
                  </div>
                  <div className="mt-1 mono text-xs ink-subtle">via entrance</div>
                </div>
              ) : (
                <div className="mt-6 tiny-label ink-subtle italic">Attendance integration pending.</div>
              )}
            </div>
          )}

          {preview?.kind === "unknown" && (
            <div data-testid="scanner-unknown-preview" className="text-center pt-6">
              <XCircle size={40} className="mx-auto text-red-500" strokeWidth={1.5}/>
              <div className="mt-4 display-heading text-xl ink">Unknown code</div>
              <div className="mt-2 tiny-label ink-subtle mono">"{preview.data.code}"</div>
              <div className="mt-4 tiny-label ink-subtle italic">Switch to Walk-in ID tab.</div>
            </div>
          )}

          {result && (
            <>
              <div className={`flex items-center gap-3 ${result.allowed ? "text-emerald-500" : "text-red-500"}`}>
                {result.allowed
                  ? <CheckCircle2 size={48} data-testid="scanner-verdict-allowed"/>
                  : <XCircle size={48} data-testid="scanner-verdict-denied"/>}
                <div>
                  <div className="display-heading text-3xl uppercase tracking-widest">
                    {result.allowed ? "Allowed" : "Denied"}
                  </div>
                  {result.override_used && (
                    <div className="tiny-label text-amber-500" data-testid="scanner-override-badge">Manager override</div>
                  )}
                </div>
              </div>

              {result.allowed && result.heads_admitted > 0 && (
                <div className="mt-4 rounded-md hairline border p-3 bg-emerald-500/5 border-emerald-500/30">
                  <div className="tiny-label text-emerald-600">Heads admitted</div>
                  <div className="mt-1 mono text-3xl font-bold text-emerald-600" data-testid="scanner-heads-admitted">{result.heads_admitted}</div>
                </div>
              )}

              {(result.reservation || result.guest_list_entry || result.ticket) && (
                <div className="mt-4 pt-3 border-t hairline-soft">
                  {result.reservation && (
                    <>
                      <div className="tiny-label ink-subtle">Reservation</div>
                      <div className="mt-1 display-heading text-lg ink">{result.reservation.customer_name}</div>
                      <div className="mono text-xs ink-subtle">{result.reservation.confirmation_code}</div>
                    </>
                  )}
                  {result.guest_list_entry && (
                    <>
                      <div className="tiny-label ink-subtle">Guest list</div>
                      <div className="mt-1 display-heading text-lg ink">{result.guest_list_entry.name}</div>
                      <div className="tiny-label ink-subtle mt-1">
                        {result.guest_list_entry.list_name}
                        {result.guest_list_entry.promoter_name && ` · ${result.guest_list_entry.promoter_name}`}
                      </div>
                    </>
                  )}
                  {result.ticket && (
                    <>
                      <div className="tiny-label ink-subtle">Ticket</div>
                      <div className="mt-1 display-heading text-lg ink">{result.ticket.holder_name || "—"}</div>
                      <div className="mono text-xs ink-subtle">{result.ticket.code} · {result.ticket.type_name}</div>
                    </>
                  )}
                </div>
              )}

              {result.denial && (
                <div className="mt-4 border border-red-500/30 rounded-md p-3 text-sm bg-red-500/5">
                  <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-red-500">Reason</div>
                  <div className="mt-1 ink">{REASON_LABEL[result.denial.reason] || result.denial.reason}</div>
                  {result.watchlist_hit && (
                    <div className="mt-2 text-xs ink-subtle italic">
                      Watchlist notes: {result.watchlist_hit.reason}
                    </div>
                  )}
                  {result.denial.allow_override && !result.allowed && hasPerm("entrance.override") && (
                    <div className="mt-3">
                      {!override.open ? (
                        <button onClick={() => setOverride({ open: true, reason: "" })}
                                data-testid="scanner-override-open"
                                className="text-[10px] uppercase tracking-[0.24em] font-bold ink hover:underline">
                          Manager override…
                        </button>
                      ) : (
                        <div className="space-y-2">
                          <textarea rows={2} placeholder="Override reason (required)"
                                    value={override.reason}
                                    onChange={(e) => setOverride({ ...override, reason: e.target.value })}
                                    data-testid="scanner-override-reason"
                                    className="w-full rounded-md border hairline surface-card p-2 ink text-xs"/>
                          <div className="flex gap-2">
                            <button onClick={() => setOverride({ open: false, reason: "" })}
                                    className="btn-pill btn-pill-outline h-8 text-[10px]">Cancel</button>
                            <button onClick={submitOverride}
                                    disabled={!override.reason.trim()}
                                    data-testid="scanner-override-confirm"
                                    className="btn-pill btn-pill-solid h-8 text-[10px] disabled:opacity-50">
                              Override &amp; admit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ==================================================================
// Recent scans strip
// ==================================================================

function RecentScans({ scans }) {
  if (!scans.length) return null;
  return (
    <div className="mt-10 animate-fade-up animate-fade-up-3">
      <div className="section-eyebrow flex items-center gap-2 mb-3"><Activity size={12}/> Recent scans</div>
      <div className="border hairline rounded-md divide-y hairline-soft overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-foreground/[0.03] tiny-label">
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Guest</div>
          <div className="col-span-2">Verdict</div>
          <div className="col-span-3">Reason</div>
          <div className="col-span-2 text-right">When</div>
        </div>
        {scans.slice(0, 8).map((s) => (
          <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-2 text-xs" data-testid={`scan-row-${s.id}`}>
            <div className="col-span-2 tiny-label">
              {s.type === "ticket" ? <Ticket size={12} className="inline mr-1 -mt-0.5"/>
                : s.type === "walkin" ? <UserRoundCheck size={12} className="inline mr-1 -mt-0.5"/>
                : s.type === "guest_list" ? <UserRoundCheck size={12} className="inline mr-1 -mt-0.5"/>
                : s.type === "reservation" ? <DoorOpen size={12} className="inline mr-1 -mt-0.5"/>
                : <DoorClosed size={12} className="inline mr-1 -mt-0.5"/>}
              {s.type}
            </div>
            <div className="col-span-3 ink truncate">{s.ticket_holder || s.id_verification?.full_name || "—"}</div>
            <div className={`col-span-2 text-[10px] uppercase tracking-[0.22em] font-bold ${s.action === "allowed" ? "text-emerald-500" : s.action === "denied" ? "text-red-500" : "ink-subtle"}`}>{s.action}</div>
            <div className="col-span-3 ink-subtle truncate italic">{s.reason ? REASON_LABEL[s.reason] || s.reason : "—"}</div>
            <div className="col-span-2 text-right mono ink-subtle">{s.scanned_at ? new Date(s.scanned_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ==================================================================
// Tiny helpers
// ==================================================================

function MetricMini({ k, v, tone }) {
  const color = tone === "emerald" ? "text-emerald-500" : tone === "red" ? "text-red-500" : "ink";
  return (
    <div>
      <div className="tiny-label">{k}</div>
      <div className={`mt-1 mono text-2xl font-semibold ${color}`}>{v}</div>
    </div>
  );
}

function FactRow({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="tiny-label">{k}</div>
      <div className="mono ink text-xs">{v ?? "—"}</div>
    </div>
  );
}
